/**
 * One winds-aloft fill per plan, shared by the two places that show it: the
 * Formular's route section (the W/V column and its freshness) and the map's
 * route panel (the per-leg W/V · temperature line).
 *
 * It wraps the whole editor — map AND form — rather than the form alone the way
 * {@link WxProvider} does, because the pilot draws the route in one half and
 * reads the answer in the other. Everything it produces that belongs to the plan
 * lands in the PlanStore; what stays here is the part the plan deliberately does
 * NOT carry: the temperature per leg. There is no cell for it on the LBA form, so
 * it lives for as long as the tab does and is gone after a reload — the W/V
 * cells and the fill's provenance survive, which is what a printed plan is made
 * of.
 *
 * **It fetches by itself.** There is no „Wind laden“ any more: the forecast is a
 * fact about the route and the clock, so it is read whenever one of the two
 * moves — the leg midpoints or altitudes (the fingerprint the map already keeps
 * for the *veraltet* mark), or the departure hour the plan is flown in. One
 * debounce covers a whole drag; an hour-old answer is re-read when the pilot
 * comes back to the tab; and the ⟳ in the section header forces it in between.
 */
import {
  createContext, useContext, useMemo, useState, type ReactNode,
} from 'react';

import { usePublishFreshness } from './freshness';
import { usePlanRev, usePlanStore } from './planContext';
import { useAutoLoad } from './useAutoLoad';
import {
  loadWinds, staleLegs, windPlan, windsSignature, type WindLeg, type WindsLoad,
} from './winds';

/** One debounce for a whole gesture — a dragged waypoint is a dozen commits. */
const DEBOUNCE_MS = 1500;
/** An hour-old forecast is re-read when the pilot comes back to the tab. */
const STALE_MS = 60 * 60_000;

export interface WindsView {
  /** what the last fetch in THIS session said, by leg; empty before the first one */
  legs: Map<number, WindLeg>;
  /** the whole outcome of that fetch (what was unavailable, what was kept, …) */
  load: WindsLoad | null;
  busy: boolean;
  error: unknown;
  /** how many legs the route currently has that could carry a wind */
  count: number;
  /** legs whose midpoint or altitude has moved since the stored fetch */
  stale: Set<number>;
  /** read it again now — the section header's ⟳ */
  refresh(): void;
}

const WindsContext = createContext<WindsView | null>(null);

export function WindsProvider({ children }: { children: ReactNode }) {
  const store = usePlanStore();
  const rev = usePlanRev();
  const [load, setLoad] = useState<WindsLoad | null>(null);

  /**
   * What the route would ask right now — recomputed on every document revision,
   * because the answer depends on the waypoints AND on the form (Höhe/FL, the
   * leg times, Datum/ETD). It costs no request: it is the comparison that decides
   * which legs are stale and whether anything is worth asking at all.
   */
  // `store` is a stable singleton whose identity never moves — `rev` is what changes
  const plan = useMemo(
    () => windPlan(store.route?.waypoints ?? [], id => store.val(id), new Date()),
    [store, rev],
  );

  const stale = useMemo(
    () => staleLegs(store.windsStatus()?.fp, plan.points),
    [store, plan, rev],
  );

  /**
   * What a fetch would ask about (see {@link windsSignature}): the leg midpoints
   * and altitudes, plus the hour the flight starts in.
   */
  const signature = useMemo(
    () => windsSignature(store.route?.waypoints ?? [], id => store.val(id), new Date()),
    // `rev` is the form's change signal: the altitudes and Datum/ETD live in it
    [store, rev],
  );

  const auto = useAutoLoad({
    signature,
    debounceMs: DEBOUNCE_MS,
    staleMs: STALE_MS,
    run: async ({ signal }) => {
      const res = await loadWinds(store, { signal });
      // null = the route lost its last leg between the schedule and the call
      if (res) setLoad(res);
    },
  });

  usePublishFreshness(
    'winds',
    useMemo(
      () => ({ busy: auto.busy, error: auto.error, at: auto.at, refresh: auto.refresh }),
      [auto.busy, auto.error, auto.at, auto.refresh],
    ),
  );

  const value = useMemo<WindsView>(
    () => ({
      legs: load?.legs ?? new Map(),
      load,
      busy: auto.busy,
      error: auto.error,
      count: plan.points.length,
      stale,
      refresh: auto.refresh,
    }),
    [load, auto.busy, auto.error, auto.refresh, plan.points.length, stale],
  );

  return <WindsContext.Provider value={value}>{children}</WindsContext.Provider>;
}

/** null outside a {@link WindsProvider} — the shared read-only view never fetches. */
export function useWinds(): WindsView | null {
  return useContext(WindsContext);
}
